import { DayAvailability } from '@/components/availability/TimeRangeManager';

export interface EnhancedBusinessHours {
  monday: DayAvailability;
  tuesday: DayAvailability;
  wednesday: DayAvailability;
  thursday: DayAvailability;
  friday: DayAvailability;
  saturday: DayAvailability;
  sunday: DayAvailability;
}

export const defaultBusinessHours: EnhancedBusinessHours = {
  monday: { available: true, timeRanges: [{ start: "09:00", end: "17:00" }] },
  tuesday: { available: true, timeRanges: [{ start: "09:00", end: "17:00" }] },
  wednesday: { available: true, timeRanges: [{ start: "09:00", end: "17:00" }] },
  thursday: { available: true, timeRanges: [{ start: "09:00", end: "17:00" }] },
  friday: { available: true, timeRanges: [{ start: "09:00", end: "17:00" }] },
  saturday: { available: false, timeRanges: [] },
  sunday: { available: false, timeRanges: [] }
};

// Team member availability uses the same structure as business hours
export type TeamMemberAvailability = EnhancedBusinessHours;

export interface TimeRange {
  start: string;
  end: string;
}

// Normalize team member availability (validates and fills missing days with defaults)
export const normalizeTeamMemberAvailability = (availability: unknown): TeamMemberAvailability => {
  return normalizeBusinessHours(availability);
};

// Validate and normalize business hours format
export const normalizeBusinessHours = (hours: unknown): EnhancedBusinessHours => {
  const defaultHours: EnhancedBusinessHours = {
    monday: { available: true, timeRanges: [{ start: "09:00", end: "17:00" }] },
    tuesday: { available: true, timeRanges: [{ start: "09:00", end: "17:00" }] },
    wednesday: { available: true, timeRanges: [{ start: "09:00", end: "17:00" }] },
    thursday: { available: true, timeRanges: [{ start: "09:00", end: "17:00" }] },
    friday: { available: true, timeRanges: [{ start: "09:00", end: "17:00" }] },
    saturday: { available: false, timeRanges: [] },
    sunday: { available: false, timeRanges: [] }
  };

  if (!hours || typeof hours !== 'object') return defaultHours;

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
  const hoursObj = hours as Record<string, unknown>;

  // Validate and ensure all days have valid structure
  const validated = { ...defaultHours };
  days.forEach(day => {
    const dayData = hoursObj[day];
    if (dayData && typeof dayData === 'object' && dayData !== null) {
      const dayObj = dayData as Record<string, unknown>;
      validated[day] = {
        available: typeof dayObj.available === 'boolean' ? dayObj.available : false,
        timeRanges: Array.isArray(dayObj.timeRanges) ? dayObj.timeRanges : []
      };
    }
  });
  return validated;
};

// Check if a time falls within business hours
export const isWithinBusinessHours = (
  time: string, 
  day: keyof EnhancedBusinessHours, 
  businessHours: EnhancedBusinessHours
): boolean => {
  const dayHours = businessHours[day];
  
  if (!dayHours.available) return false;
  
  return dayHours.timeRanges.some(range => {
    return time >= range.start && time <= range.end;
  });
};

// Get all available time slots for a day
export const getAvailableTimeSlots = (
  day: keyof EnhancedBusinessHours,
  businessHours: EnhancedBusinessHours,
  slotDuration: number = 30 // minutes
): string[] => {
  const dayHours = businessHours[day];
  
  if (!dayHours.available) return [];
  
  const slots: string[] = [];
  
  dayHours.timeRanges.forEach(range => {
    const startTime = parseTime(range.start);
    const endTime = parseTime(range.end);
    
    let currentTime = startTime;
    
    while (currentTime < endTime) {
      slots.push(formatTime(currentTime));
      currentTime += slotDuration;
    }
  });
  
  return slots;
};

// Parse time string to minutes since midnight
const parseTime = (timeString: string): number => {
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours * 60 + minutes;
};

// Format minutes since midnight to time string
const formatTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

// Derive workingDays array from businessHours configuration
// Returns array of day indices where day is available: 0=Sunday, 1=Monday, ..., 6=Saturday
export const getWorkingDaysFromBusinessHours = (businessHours: EnhancedBusinessHours): number[] => {
  const dayMapping: Record<keyof EnhancedBusinessHours, number> = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6
  };

  const workingDays: number[] = [];

  (Object.keys(dayMapping) as Array<keyof EnhancedBusinessHours>).forEach(day => {
    if (businessHours[day]?.available) {
      workingDays.push(dayMapping[day]);
    }
  });

  return workingDays;
};

// Check if a date is a working day based on workingDays array
// workingDays is an array of day indices: 0=Sunday, 1=Monday, ..., 6=Saturday
export const isWorkingDay = (date: Date, workingDays: number[] = [1, 2, 3, 4, 5]): boolean => {
  const dayIndex = date.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday
  return workingDays.includes(dayIndex);
};

// Check if business hours have any overlapping time ranges
export const validateBusinessHours = (businessHours: EnhancedBusinessHours): string[] => {
  const errors: string[] = [];

  Object.entries(businessHours).forEach(([day, dayHours]) => {
    if (!dayHours.available) return;

    // Check for overlapping time ranges
    dayHours.timeRanges.forEach((range: TimeRange, index: number) => {
      // Validate individual range
      if (range.start >= range.end) {
        errors.push(`${day}: Start time must be before end time`);
      }

      // Check for overlaps with other ranges
      dayHours.timeRanges.slice(index + 1).forEach((otherRange: TimeRange, otherIndex: number) => {
        if (
          (range.start < otherRange.end && range.end > otherRange.start) ||
          (otherRange.start < range.end && otherRange.end > range.start)
        ) {
          errors.push(`${day}: Time ranges ${index + 1} and ${otherIndex + index + 2} overlap`);
        }
      });
    });
  });

  return errors;
};

// Get the next available time slot
export const getNextAvailableSlot = (
  businessHours: EnhancedBusinessHours,
  fromDate: Date,
  slotDuration: number = 30
): Date | null => {
  const daysOfWeek: (keyof EnhancedBusinessHours)[] = [
    'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'
  ];
  
  const currentDate = new Date(fromDate);
  
  // Check up to 7 days ahead
  for (let i = 0; i < 7; i++) {
    const dayName = daysOfWeek[currentDate.getDay()];
    const availableSlots = getAvailableTimeSlots(dayName, businessHours, slotDuration);
    
    if (availableSlots.length > 0) {
      // Find first available slot for this day
      const currentTimeString = `${currentDate.getHours().toString().padStart(2, '0')}:${currentDate.getMinutes().toString().padStart(2, '0')}`;
      
      const nextSlot = availableSlots.find(slot => 
        i === 0 ? slot > currentTimeString : true
      );
      
      if (nextSlot) {
        const [hours, minutes] = nextSlot.split(':').map(Number);
        const nextAvailable = new Date(currentDate);
        nextAvailable.setHours(hours, minutes, 0, 0);
        return nextAvailable;
      }
    }
    
    // Move to next day
    currentDate.setDate(currentDate.getDate() + 1);
    currentDate.setHours(0, 0, 0, 0);
  }
  
  return null;
};

// Team member availability validation functions
export const isTeamMemberAvailable = (
  time: string,
  day: keyof EnhancedBusinessHours,
  availability: TeamMemberAvailability
): boolean => {
  return isWithinBusinessHours(time, day, availability);
};

export const getTeamMemberAvailableSlots = (
  day: keyof EnhancedBusinessHours,
  availability: TeamMemberAvailability,
  slotDuration: number = 30
): string[] => {
  return getAvailableTimeSlots(day, availability, slotDuration);
};

// Check if an appointment time range fits within team member availability
export const isAppointmentWithinTeamMemberAvailability = (
  startTime: Date,
  endTime: Date,
  availability: TeamMemberAvailability
): boolean => {
  const dayNames: (keyof EnhancedBusinessHours)[] = [
    'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'
  ];

  const dayName = dayNames[startTime.getDay()];
  const startTimeString = `${startTime.getHours().toString().padStart(2, '0')}:${startTime.getMinutes().toString().padStart(2, '0')}`;
  const endTimeString = `${endTime.getHours().toString().padStart(2, '0')}:${endTime.getMinutes().toString().padStart(2, '0')}`;

  // Check if both start and end times are within available hours
  return isTeamMemberAvailable(startTimeString, dayName, availability) &&
         isTeamMemberAvailable(endTimeString, dayName, availability);
};

// Minimal blocked period type for checking day availability
export interface MinimalBlockedPeriod {
  start_time: string;
  end_time: string;
  applies_to: 'all' | 'specific_team_members' | 'specific_resources';
}

// Check if a day is fully blocked by organization-wide blockers
// Returns true if all business hours on this day are covered by blockers that apply to 'all'
export const isDayFullyBlocked = (
  date: Date,
  businessHours: EnhancedBusinessHours,
  blockedPeriods: MinimalBlockedPeriod[]
): boolean => {
  // Get day name (sunday, monday, etc.)
  const dayNames: (keyof EnhancedBusinessHours)[] = [
    'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'
  ];
  const dayName = dayNames[date.getDay()];
  const dayHours = businessHours[dayName];

  // If the day isn't available anyway, it's not considered "blocked" (it's just closed)
  if (!dayHours.available || dayHours.timeRanges.length === 0) {
    return false;
  }

  // Get start and end of the day in UTC (to match blocker timestamps which are in UTC)
  const dayStart = new Date(date);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(date);
  dayEnd.setHours(23, 59, 59, 999);

  // Filter blockers that:
  // 1. Apply to all (organization-wide)
  // 2. Overlap with this day
  const relevantBlockers = blockedPeriods.filter(blocker => {
    if (blocker.applies_to !== 'all') return false;

    const blockerStart = new Date(blocker.start_time);
    const blockerEnd = new Date(blocker.end_time);

    // Check if blocker overlaps with the day
    return blockerStart <= dayEnd && blockerEnd >= dayStart;
  });

  if (relevantBlockers.length === 0) return false;

  // Get the business hours time ranges for this day
  // Convert them to actual Date objects for this specific date
  const businessTimeRanges = dayHours.timeRanges.map(range => {
    const [startHours, startMinutes] = range.start.split(':').map(Number);
    const [endHours, endMinutes] = range.end.split(':').map(Number);

    const start = new Date(date);
    start.setHours(startHours, startMinutes, 0, 0);

    const end = new Date(date);
    end.setHours(endHours, endMinutes, 0, 0);

    return { start, end };
  });

  // Check if all business hours are covered by blockers
  // For each business time range, check if it's fully covered by at least one blocker
  return businessTimeRanges.every(businessRange => {
    return relevantBlockers.some(blocker => {
      const blockerStart = new Date(blocker.start_time);
      const blockerEnd = new Date(blocker.end_time);

      // Check if the blocker completely covers this business time range
      return blockerStart <= businessRange.start && blockerEnd >= businessRange.end;
    });
  });
};